import {
  notify,
  mediaToBase64,
  fetchMediaData,
  manageErrorLogs,
  mediaURLtoBlob,
  checkIsValidArray,
} from "./commonFunction";
import {
  DB,
  insertEntryInDB,
  updateEntryInDB,
  getEntryByFieldFromDB,
  getMultipleEntriesFromDB,
  clearTablesWithTran,
} from "./db";
import { apiEndpoints, SYNC_TYPE } from "./constants";
import {
  reqToFetchDataProcess,
  reqToInitialSyncProcess,
  reqToResetApiMessage,
  reqToStartSyncProcess,
  reqToUploadMediaFile,
  reqToGetAddressFromLatLong,
  reqToGetRestaurantDetails,
  reqToGetRestaurantReports,
  reqToSubmitRestaurantDetails,
  reqToSubmitTaskSummaryNotes,
} from "../store/slice/offlineSlice";
import {
  insertRestaurantDetailsInDB,
  insertRestaurantReportsInDB,
} from "./offlineDBQuery";
import { reqToEnableAllDataIDBCheck } from "../store/slice/indexedDBSlice";

const executeAPICall = async (apiCall, dispatch, resetAction = null) => {
  try {
    const result = await apiCall;

    if (resetAction) {
      dispatch(resetAction(""));
    }

    if (!result) return false;

    if (result && result.status && result.status !== 200) {
      return false;
    }

    return true;
  } catch (error) {
    manageErrorLogs(error);
    return false;
  }
};

// FETCH LATEST DATA
export const getLatestDataFromDB = async (type, dispatch, userData) => {
  let allSuccess = true;
  try {
    if (
      type === SYNC_TYPE.LOGIN ||
      type === SYNC_TYPE.SYNC ||
      type === SYNC_TYPE.ISNMONTH ||
      type === SYNC_TYPE.FINALUPLOAD
    ) {
      dispatch(reqToFetchDataProcess(true));
      dispatch(reqToResetApiMessage(""));
    }
    if (
      type === SYNC_TYPE.SYNC ||
      type === SYNC_TYPE.ISNMONTH ||
      type === SYNC_TYPE.AUTOREFRESH
    ) {
      const apiCallsList = [
        () =>
          executeAPICall(
            getLatestRestaurantDetailsFromDB(dispatch),
            dispatch,
            reqToResetApiMessage
          ),
        () =>
          executeAPICall(
            getLatestRestaurantReportsFromDB(dispatch),
            dispatch,
            reqToResetApiMessage
          ),
      ];

      // Execute API calls sequentially (one by one)
      for (const apiCall of apiCallsList) {
        if (apiCall) {
          try {
            const result = await apiCall();
            if (result === false) {
              allSuccess = false;
            }
          } catch (error) {
            allSuccess = false;
          }
        }
      }
    } else if (type === SYNC_TYPE.LOGIN || type === SYNC_TYPE.FINALUPLOAD) {
      const apiCalls = [];
      apiCalls.push(() =>
        executeAPICall(
          getLatestRestaurantDetailsFromDB(dispatch),
          dispatch,
          reqToResetApiMessage
        )
      );
      apiCalls.push(() =>
        executeAPICall(
          getLatestRestaurantReportsFromDB(dispatch),
          dispatch,
          reqToResetApiMessage
        )
      );

      // Execute API calls sequentially (one by one)
      for (const apiCall of apiCalls) {
        try {
          const result = await apiCall();
          if (result === false) {
            allSuccess = false;
          }
        } catch (error) {
          allSuccess = false;
        }
      }
    }
    dispatch(reqToFetchDataProcess(false));
    dispatch(reqToInitialSyncProcess(false));
    dispatch(reqToEnableAllDataIDBCheck({}));
    return allSuccess;
  } catch (error) {
    dispatch(reqToFetchDataProcess(false));
    dispatch(reqToInitialSyncProcess(false));
    dispatch(reqToEnableAllDataIDBCheck({}));
    throw error;
  }
};
export const getLatestRestaurantDetailsFromDB = async (dispatch) => {
  return new Promise((resolve, reject) => {
    dispatch(
      reqToGetRestaurantDetails({
        onSuccess: async (res) => {
          if (res?.status !== 200) return resolve(res);
          if (checkIsValidArray(res?.data?.company)) {
            fetchAndStoreReportsMedia(res?.data?.company);
          }
          if (checkIsValidArray(res?.data?.localGov)) {
            fetchAndStoreReportsMedia(res?.data?.localGov);
          }
          await insertRestaurantDetailsInDB(res?.data);
          resolve(res);
        },
        onFailure: (err) => {
          reject(err);
        },
        dispatch,
      })
    );
  }).catch((_) => {
    notify("Failed to fetch restaurant details!");
  });
};
const fetchAndStoreReportsMedia = async (restaurantList) => {
  if (!checkIsValidArray(restaurantList)) return;

  return restaurantList.map((rest) => {
    if (checkIsValidArray(rest?.location)) {
      rest?.location.map((loc) => {
        if (checkIsValidArray(loc?.items)) {
          loc?.items.map((item) => {
            if (checkIsValidArray(item?.sub_items)) {
              item?.sub_items.map((subItem) => {
                if (checkIsValidArray(subItem?.item_report)) {
                  subItem?.item_report.map((report) => {
                    updateMediaFileInDB(report?.image, "attachment");
                  });
                  return subItem;
                } else {
                  return subItem;
                }
              });
            } else {
              return item;
            }
          });
        } else {
          return loc;
        }
      });
    } else {
      return rest;
    }
  });
};
export const getLatestRestaurantReportsFromDB = async (dispatch) => {
  return new Promise((resolve, reject) => {
    dispatch(
      reqToGetRestaurantReports({
        onSuccess: async (res) => {
          if (res?.status !== 200) return resolve(res);
          await insertRestaurantReportsInDB(res?.data);
          resolve(res);
        },
        onFailure: (err) => {
          reject(err);
        },
        dispatch,
      })
    );
  }).catch((_) => {
    notify("Failed to fetch restaurant reports!");
  });
};

// SUBMIT DATA
export const handleOfflineDataSyncing = async (
  dispatch,
  sync,
  syncType = SYNC_TYPE.SYNC
) => {
  sync && dispatch(reqToStartSyncProcess(true));
  dispatch(reqToResetApiMessage(""));
  return new Promise(async (resolve, reject) => {
    try {
      const fetchData = await getMultipleEntriesFromDB([
        "addedRestaurantDetails",
        "addedRestaurantTaskNotes",
        "userData",
      ]);
      if (!fetchData || Object.keys(fetchData)?.length === 0) {
        throw new Error(`No data found in DB!`);
      }
      const { addedRestaurantDetails, addedRestaurantTaskNotes, userData } =
        fetchData;
      if (
        addedRestaurantDetails?.length > 0 ||
        addedRestaurantTaskNotes?.length > 0
      ) {
        await submitIndexedDbData(
          dispatch,
          addedRestaurantDetails,
          addedRestaurantTaskNotes,
          userData,
          sync,
          syncType
        )
          .then(async (res) => {
            dispatch(reqToStartSyncProcess(false));
            dispatch(reqToInitialSyncProcess(false));
            dispatch(reqToEnableAllDataIDBCheck({}));
            await clearTablesWithTran("errorLogs");
            resolve(res);
          })
          .catch((error) => {
            manageErrorLogs(error);
            dispatch(reqToStartSyncProcess(false));
            dispatch(reqToInitialSyncProcess(false));
            dispatch(reqToEnableAllDataIDBCheck({}));
            reject(error);
          });
      } else {
        if (
          sync &&
          (syncType === SYNC_TYPE.SYNC ||
            syncType === SYNC_TYPE.ISNMONTH ||
            syncType === SYNC_TYPE.FINALUPLOAD)
        ) {
          getLatestDataFromDB(syncType, dispatch, userData);
        } else if (syncType === SYNC_TYPE.AUTOREFRESH) {
          getLatestDataFromDB(syncType, dispatch, userData);
        }
        dispatch(reqToStartSyncProcess(false));
        dispatch(reqToInitialSyncProcess(false));
        await clearTablesWithTran("errorLogs");
        resolve();
      }
    } catch (error) {
      manageErrorLogs(error);
      dispatch(reqToStartSyncProcess(false));
      dispatch(reqToInitialSyncProcess(false));
      dispatch(reqToEnableAllDataIDBCheck({}));
      reject(error);
    }
  });
};
export const submitIndexedDbData = async (
  dispatch,
  addedRestaurantDetails,
  addedRestaurantTaskNotes,
  userData,
  sync,
  syncType
) => {
  return new Promise(async (resolve, reject) => {
    const promises = [];
    const isSync =
      syncType === SYNC_TYPE.SYNC || syncType === SYNC_TYPE.AUTOREFRESH;
    if ((sync || (!sync && isSync)) && addedRestaurantDetails?.length > 0) {
      promises.push(
        submitRestaurantDetailsWhenOnline(
          dispatch,
          addedRestaurantDetails,
          "addedRestaurantDetails"
        )
      );
    }
    if ((sync || (!sync && isSync)) && addedRestaurantTaskNotes?.length > 0) {
      promises.push(
        submitTaskSummaryNoteWhenOnline(
          dispatch,
          addedRestaurantTaskNotes,
          "addedRestaurantTaskNotes"
        )
      );
    }
    await Promise.allSettled(promises)
      .then(async (results) => {
        const isRejected = results.find((item) => item.status === "rejected");
        if (isRejected && isRejected?.reason) {
          reject(isRejected.reason);
        } else {
          const fetchPromises = results.map(async (result) => {
            if (sync && !isRejected && syncType === SYNC_TYPE.SYNC) {
              if (["addedRestaurantDetails"].includes(result?.value)) {
                await getLatestRestaurantDetailsFromDB(dispatch, "");
                await getLatestRestaurantReportsFromDB(dispatch, "");
              }
            }
          });
          await Promise.all(fetchPromises);
          resolve(results);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const submitRestaurantDetailsWhenOnline = async (
  dispatch,
  addedRestaurantDetails,
  type
) => {
  const promises = [];
  for (const item of addedRestaurantDetails) {
    try {
      const result = await new Promise(async (resolve, reject) => {
        if (item?.image?.size || item?.base64?.includes("base64")) {
          const blobData =
            item?.image && !item?.image?.includes("base64")
              ? item?.image
              : await mediaURLtoBlob(item?.base64);
          const image = await processMediaFile(blobData, dispatch);
          item.image = image;
          item.base64 = image;
          await DB.addedRestaurantDetails.update(item?.id, item);
        }

        dispatch(
          reqToSubmitRestaurantDetails({
            data: {
              ...item,
              recode_time: item?.recode_time || new Date(),
            },
            onSuccess: async (res) => {
              await DB.addedRestaurantDetails.delete(item?.id);
              resolve(res);
            },
            onFailure: async (error) => {
              if (
                error &&
                error?.response &&
                error?.response?.status === 400 &&
                error?.response?.data?.toLowerCase() === "already exist"
              ) {
                await DB.addedRestaurantDetails.delete(item?.id);
                resolve();
              } else {
                await insertEntryInDB("errorLogs", {
                  data: item,
                  api: apiEndpoints.submitRestaurantDetails,
                  error: JSON.stringify(error),
                  date: new Date(),
                  response: JSON.stringify({
                    data: error?.response?.data,
                    status: error?.response?.status,
                    statusText: error?.response?.statusText,
                  }),
                });
                reject(error);
              }
            },
          })
        );
      });
      promises.push({ data: result, status: true });
    } catch (error) {
      promises.push({ data: error, status: false });
    }
  }
  const failedCalls = promises.filter((result) => !result.status);
  if (failedCalls?.length === 0) {
    return Promise.resolve(type);
  } else {
    return Promise.reject(failedCalls[0].data);
  }
};

export const submitTaskSummaryNoteWhenOnline = async (
  dispatch,
  addedRestaurantTaskNotes,
  type
) => {
  const promises = [];
  for (const item of addedRestaurantTaskNotes) {
    try {
      const result = await new Promise(async (resolve, reject) => {
        if (item?.attachments?.size || item?.base64?.includes("base64")) {
          const blobData =
            item?.attachments && !item?.attachments?.includes("base64")
              ? item?.attachments
              : await mediaURLtoBlob(item?.base64);
          const attachments = await processMediaFile(blobData, dispatch);
          item.attachments = attachments;
          item.base64 = attachments;
          await DB.addedRestaurantTaskNotes.update(item?.id, item);
        }

        dispatch(
          reqToSubmitTaskSummaryNotes({
            data: {
              ...item,
              record_date: item?.record_date || new Date(),
            },
            onSuccess: async (res) => {
              await DB.addedRestaurantTaskNotes.delete(item?.id);
              resolve(res);
            },
            onFailure: async (error) => {
              if (
                error &&
                error?.response &&
                error?.response?.status === 400 &&
                error?.response?.data?.toLowerCase() === "already exist"
              ) {
                await DB.addedRestaurantTaskNotes.delete(item?.id);
                resolve();
              } else {
                await insertEntryInDB("errorLogs", {
                  data: item,
                  api: apiEndpoints.addRestaurantTaskNotes,
                  error: JSON.stringify(error),
                  date: new Date(),
                  response: JSON.stringify({
                    data: error?.response?.data,
                    status: error?.response?.status,
                    statusText: error?.response?.statusText,
                  }),
                });
                reject(error);
              }
            },
          })
        );
      });
      promises.push({ data: result, status: true });
    } catch (error) {
      promises.push({ data: error, status: false });
    }
  }
  const failedCalls = promises.filter((result) => !result.status);
  if (failedCalls?.length === 0) {
    return Promise.resolve(type);
  } else {
    return Promise.reject(failedCalls[0].data);
  }
};

export const processMediaFile = (url, dispatch, fileName) => {
  return new Promise((resolve, reject) => {
    if (url && url?.size) {
      const formData = new FormData();
      if (fileName) {
        formData.append("file", url, fileName);
      } else {
        const fileURL = url?.type?.split("/")[1];
        const fileURL2 = ["svg", "webp"].includes(fileURL) ? "png" : fileURL;
        formData.append("file", url, `attachment.${fileURL2 || "png"}`);
      }
      dispatch(
        reqToUploadMediaFile({
          data: formData,
          onSuccess: async (res) => {
            if (res && res?.data?.length > 0) {
              if (url?.name && url?.name.includes(".xls")) {
                let excelUrl = res?.data[0]?.videoUrl.split("/");
                let excelName = excelUrl && excelUrl[excelUrl?.length - 1];
                resolve(excelName);
              } else {
                let imagesUrl = res?.data[0]?.imageUrlBigUrl.split("/");
                let imageName = imagesUrl && imagesUrl[imagesUrl?.length - 1];
                await updateMediaFileInDB(imageName, "sign", url);
                resolve(imageName);
              }
            }
          },
          onFailure: async (error) => {
            const base64 = await mediaToBase64(url);
            await insertEntryInDB("errorLogs", {
              data: base64 ? "file is available" : "",
              api: apiEndpoints.media,
              error: JSON.stringify(error),
              date: new Date(),
              response: JSON.stringify({
                data: error?.response?.data,
                status: error?.response?.status,
                statusText: error?.response?.statusText,
              }),
            });
            reject(error);
          },
        })
      );
    } else {
      resolve(url);
    }
  });
};
export const reqToGetLocation = async (newData, dispatch) => {
  return new Promise((resolve, reject) => {
    const reqObj = {
      latitude: newData.latitude,
      longitude: newData.longitude,
    };
    dispatch(
      reqToGetAddressFromLatLong({
        data: reqObj,
        onSuccess: (response) => {
          const location = {
            address: response.data,
            latitude: newData.latitude,
            longitude: newData.longitude,
          };
          resolve(location);
        },
        onFailure: async (error) => {
          const location = {
            address: "",
            latitude: newData?.latitude || "",
            longitude: newData?.longitude || "",
          };
          resolve(location);
          await insertEntryInDB("errorLogs", {
            data: reqObj,
            api: apiEndpoints.getLocation,
            error: JSON.stringify(error),
            date: new Date(),
            response: JSON.stringify({
              data: error?.response?.data,
              status: error?.response?.status,
              statusText: error?.response?.statusText,
            }),
          });
        },
      })
    );
  });
};

export const updateMediaFileInDB = async (file, type, blobData) => {
  if (!file || Object.keys(file)?.length === 0) return;
  try {
    const tableName =
      type === "pdf" || type === "image" ? "productImages" : "mediaFiles";
    const findFile = await getEntryByFieldFromDB(tableName, "file", file);
    if (findFile?.length === 0 || !findFile[0]?.base64) {
      const url = file?.includes("https")
        ? file
        : process.env.REACT_APP_IMAGE_URL + file;
      const blob = blobData ? blobData : await fetchMediaData(url);
      const base64 = blob ? await mediaToBase64(blob) : "";
      await updateEntryInDB(tableName, "file", file, {
        file: file,
        type: type,
        blob: "",
        base64: base64,
        date: new Date(),
      });
      return blob;
    }
  } catch (error) {
    return file;
  }
};
export const getMediaFileFromDB = async (dataList, file, base64, fetch) => {
  try {
    if (
      dataList &&
      dataList[file] &&
      !dataList[base64] &&
      !dataList[file]?.size &&
      !dataList[file]?.includes("base64")
    ) {
      const fileData = await getEntryByFieldFromDB(
        "mediaFiles",
        "file",
        dataList[file]
      );
      if (fetch === "blob") {
        const blob = fileData?.[0]?.base64
          ? await mediaURLtoBlob(fileData?.[0]?.base64)
          : "";
        return blob;
      } else {
        return fileData?.[0]?.base64 ? fileData?.[0]?.base64 : "";
      }
    } else if (dataList[base64] && dataList[base64]?.includes("base64")) {
      return dataList[base64];
    } else {
      return "";
    }
  } catch (error) {
    manageErrorLogs(error);
    return "";
  }
};
